/**
 * RideDetailDrawer — Planning board'da canlı ride chip'ine tıklayınca açılan
 * sağ-slide drawer. Üç sekme:
 *
 *   1. STOPS  → Latest CSV stops (UTF-8 parsed) — postcode bazlı detay tablosu
 *   2. MAP    → Belçika haritası + her unique postcode için marker (Leaflet)
 *   3. GEÇMİŞ → Son 24 saatte Ritmonitor history (stops tamamlama sparkline + tablo)
 *
 * Veri kaynakları:
 *   - GET /api/postnl/dagplanning/latest-csv-parsed/{ritnaam}
 *   - POST /api/postnl/dagplanning/geocode (Nominatim + MongoDB cache)
 *   - GET /api/postnl/ritmonitor/history/{ritnaam}
 */
import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, CircleMarker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BASE_LAYERS } from '../../lib/mapTiles';
import { api } from '../../lib/auth';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  X, Loader2, MapPin, Table as TableIcon, History as HistoryIcon,
  Truck, Package, AlertCircle,
} from 'lucide-react';

// Leaflet default marker fix (Webpack bundle problem) — opacity 0 default marker
// kullanmıyoruz, CircleMarker tercih ediliyor; ama backup için tanım gerekli.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TABS = [
  { id: 'stops', label: 'Stops', icon: TableIcon },
  { id: 'map', label: 'Harita', icon: MapPin },
  { id: 'history', label: 'Geçmiş', icon: HistoryIcon },
];

export function RideDetailDrawer({ ritnaam, liveSnapshot, onClose }) {
  const [tab, setTab] = useState('stops');

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="ride-detail-drawer"
    >
      <div
        className="w-full max-w-3xl h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-start gap-3">
          <Truck className="h-5 w-5 text-orange-500 mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono font-semibold text-base truncate">{ritnaam}</div>
            {liveSnapshot && (
              <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                <span>Şoför: <strong>{liveSnapshot.chauffeur || '—'}</strong></span>
                <span>·</span>
                <span>Son aksiyon: <strong>{liveSnapshot.lastAction || '—'}</strong></span>
                <span>·</span>
                <span className="text-emerald-700 dark:text-emerald-400">
                  {liveSnapshot.done}/{liveSnapshot.stops} tamamlandı ({liveSnapshot.donePct}%)
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            data-testid="ride-detail-drawer-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 border-b border-slate-200 dark:border-slate-700 flex gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                  active
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
                data-testid={`ride-detail-tab-${t.id}`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'stops' && <StopsTab ritnaam={ritnaam} />}
          {tab === 'map' && <MapTab ritnaam={ritnaam} />}
          {tab === 'history' && <HistoryTab ritnaam={ritnaam} />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// STOPS TAB — Latest parsed CSV (postcode bazlı tablo)
// ---------------------------------------------------------------------------
function StopsTab({ ritnaam }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/postnl/dagplanning/latest-csv-parsed/${encodeURIComponent(ritnaam)}`)
      .then((res) => {
        if (!cancelled) setData(res.data?.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'CSV bulunamadı');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ritnaam]);

  if (loading) return <LoaderBlock label="CSV yükleniyor…" />;
  if (error) return <ErrorBlock msg={String(error)} />;
  if (!data || !data.rows?.length) return <EmptyBlock msg="Bu rota için en son snapshot'ta CSV yok." />;

  // Postcode bazlı özet hesapla
  const byPostcode = (() => {
    const m = new Map();
    for (const row of data.rows) {
      const pc = row.Postcode || '—';
      const e = m.get(pc) || { count: 0, stuks: 0, gewicht: 0, woonplaats: row.Woonplaats };
      e.count += 1;
      e.stuks += Number((row.Stuks || '0').replace(',', '.')) || 0;
      e.gewicht += Number((row.Gewicht || '0').replace(',', '.')) || 0;
      m.set(pc, e);
    }
    return [...m.entries()].sort((a, b) => b[1].count - a[1].count);
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-xs">
        <Badge variant="outline" className="font-mono">{data.row_count} stop</Badge>
        <Badge variant="outline" className="font-mono">{byPostcode.length} postcode</Badge>
        <span className="text-slate-400">
          Snapshot: {data.portal_date} · {new Date(data.captured_at).toLocaleString('tr-TR')}
        </span>
      </div>

      {/* Postcode özet barı */}
      <Card className="p-3">
        <div className="text-[11px] font-semibold text-slate-600 mb-2">Postcode dağılımı</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
          {byPostcode.map(([pc, e]) => (
            <div key={pc} className="flex items-center gap-2 px-2 py-1 rounded bg-slate-50 dark:bg-slate-800 text-[11px]">
              <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{pc}</span>
              <span className="text-slate-500 truncate flex-1">{e.woonplaats}</span>
              <Badge className="bg-orange-100 text-orange-700 border-0 text-[10px] tabular-nums">{e.count}</Badge>
            </div>
          ))}
        </div>
      </Card>

      {/* Tam stop tablosu */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
              <tr>
                {data.headers.map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700 odd:bg-slate-50/40 dark:odd:bg-slate-900/40">
                  {data.headers.map((h) => (
                    <td key={h} className="px-2 py-1 whitespace-nowrap text-slate-700 dark:text-slate-200">
                      {r[h] || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAP TAB — Leaflet, Belçika postcode geocoding
// ---------------------------------------------------------------------------
function MapTab({ ritnaam }) {
  const [csv, setCsv] = useState(null);
  const [geo, setGeo] = useState({}); // postcode → {lat, lon, display_name}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [showRoute, setShowRoute] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setGeo({});
    api.get(`/postnl/dagplanning/latest-csv-parsed/${encodeURIComponent(ritnaam)}`)
      .then((res) => {
        if (!cancelled) setCsv(res.data?.data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || 'CSV bulunamadı');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ritnaam]);

  // Unique postcodes
  const postcodes = useMemo(() => {
    if (!csv?.rows) return [];
    const s = new Set();
    for (const r of csv.rows) {
      const p = (r.Postcode || '').trim();
      if (p) s.add(p);
    }
    return [...s];
  }, [csv]);

  // Postcode → {count, woonplaats}
  const postcodeStats = useMemo(() => {
    const m = new Map();
    if (!csv?.rows) return m;
    for (const r of csv.rows) {
      const p = (r.Postcode || '').trim();
      if (!p) continue;
      const e = m.get(p) || { count: 0, woonplaats: r.Woonplaats || '' };
      e.count += 1;
      m.set(p, e);
    }
    return m;
  }, [csv]);

  useEffect(() => {
    if (!postcodes.length) return;
    let cancelled = false;
    setGeocoding(true);
    api.post('/postnl/dagplanning/geocode', { postcodes, country: 'BE' })
      .then((res) => {
        if (!cancelled) setGeo(res.data?.data?.results || {});
      })
      .catch(() => toast.error('Geocoding başarısız'))
      .finally(() => { if (!cancelled) setGeocoding(false); });
    return () => { cancelled = true; };
  }, [postcodes]);

  // TVI-ordered route — sürücünün gerçek teslim sırasını gösterir.
  // CSV satırlarını TVI (Tijdvenster index, "11.10" → 11.10 numeric) bazında
  // sıralıyoruz; her satırın postcode'unun lat/lon'unu alıp polyline points
  // listesi oluşturuyoruz. Aynı postcode'a ardışık birden fazla stop varsa
  // zero-length segmentleri atlamak için duplicate'ları sıkıştırıyoruz.
  // NOT: Hook'lar her render'da aynı sırada çalışmalı; early return'lerden önce.
  const routeData = useMemo(() => {
    if (!csv?.rows?.length || !Object.keys(geo).length) {
      return { positions: [], orderedPostcodes: [], totalStops: 0 };
    }
    const sortable = csv.rows
      .map((row, idx) => {
        const tviRaw = (row.TVI || '').replace(',', '.').trim();
        const tvi = tviRaw && !Number.isNaN(parseFloat(tviRaw)) ? parseFloat(tviRaw) : null;
        return { row, idx, tvi };
      })
      .filter((x) => x.tvi != null)
      .sort((a, b) => a.tvi - b.tvi);
    const positions = [];
    const orderedPostcodes = [];
    let lastKey = null;
    for (const s of sortable) {
      const pc = (s.row.Postcode || '').trim();
      const g = geo[pc];
      if (!g || g.lat == null || g.lon == null) continue;
      const key = `${g.lat},${g.lon}`;
      if (key === lastKey) continue; // ardışık aynı postcode → atla
      positions.push([g.lat, g.lon]);
      orderedPostcodes.push(pc);
      lastKey = key;
    }
    return { positions, orderedPostcodes, totalStops: sortable.length };
  }, [csv, geo]);

  if (loading) return <LoaderBlock label="CSV yükleniyor…" />;
  if (error) return <ErrorBlock msg={String(error)} />;
  if (!csv?.rows?.length) return <EmptyBlock msg="Haritada gösterilecek stop yok." />;

  // Geocoded markers
  const markers = postcodes
    .map((pc) => ({ pc, geo: geo[pc], stats: postcodeStats.get(pc) }))
    .filter((m) => m.geo && m.geo.lat != null && m.geo.lon != null);

  // Center: marker'ların ortalaması, yoksa Belçika ortası
  const center = markers.length > 0
    ? [
        markers.reduce((s, m) => s + m.geo.lat, 0) / markers.length,
        markers.reduce((s, m) => s + m.geo.lon, 0) / markers.length,
      ]
    : [50.85, 4.35]; // Brüksel

  const maxCount = Math.max(1, ...markers.map((m) => m.stats?.count || 1));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300 flex-wrap">
        <Badge variant="outline" className="font-mono">{postcodes.length} postcode</Badge>
        {geocoding ? (
          <span className="text-amber-600 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Geocoding ({markers.length}/{postcodes.length})…
          </span>
        ) : (
          <span className="text-emerald-700 dark:text-emerald-400">
            {markers.length}/{postcodes.length} konum bulundu
          </span>
        )}
        {routeData.positions.length > 1 && (
          <>
            <span className="text-slate-400">·</span>
            <button
              onClick={() => setShowRoute((s) => !s)}
              className={`px-2 py-0.5 rounded border text-[10px] font-semibold transition ${
                showRoute
                  ? 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200'
                  : 'bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200'
              }`}
              data-testid="ride-detail-route-toggle"
              title="Rota çizgisi (TVI sırasıyla) göster/gizle"
            >
              {showRoute ? '✓ Rota Çizgisi' : 'Rota Çizgisi'}
            </button>
            <span className="text-slate-400 text-[10px]">{routeData.positions.length} adım · {routeData.totalStops} stop</span>
          </>
        )}
      </div>

      <div className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700" style={{ height: '60vh' }}>
        <MapContainer center={center} zoom={9} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer
            attribution={BASE_LAYERS.map.attribution}
            url={BASE_LAYERS.map.url}
          />
          {/* TVI-ordered route polyline (sürücünün gerçek teslim güzergahı) */}
          {showRoute && routeData.positions.length > 1 && (
            <Polyline
              positions={routeData.positions}
              pathOptions={{
                color: '#0ea5e9',
                weight: 3,
                opacity: 0.75,
                dashArray: '6, 8',
              }}
            />
          )}
          {markers.map((m) => {
            const r = 6 + Math.round((m.stats?.count || 1) / maxCount * 14);
            return (
              <CircleMarker
                key={m.pc}
                center={[m.geo.lat, m.geo.lon]}
                radius={r}
                pathOptions={{
                  color: '#f97316',
                  fillColor: '#fb923c',
                  fillOpacity: 0.6,
                  weight: 2,
                }}
              >
                <Tooltip direction="top" offset={[0, -5]} opacity={0.95} permanent={false}>
                  <div className="text-[11px]">
                    <div className="font-mono font-bold">{m.pc}</div>
                    <div className="text-slate-600">{m.stats?.woonplaats || '—'}</div>
                    <div className="text-orange-700 font-semibold">{m.stats?.count || 0} stop</div>
                  </div>
                </Tooltip>
              </CircleMarker>
            );
          })}
          {/* Start / End markers (rotanın ilk ve son durağı) */}
          {showRoute && routeData.positions.length > 1 && (
            <>
              <CircleMarker
                center={routeData.positions[0]}
                radius={9}
                pathOptions={{ color: '#fff', fillColor: '#10b981', fillOpacity: 1, weight: 3 }}
              >
                <Tooltip direction="top" offset={[0, -8]} permanent>
                  <span className="text-[10px] font-bold text-emerald-700">BAŞLA · {routeData.orderedPostcodes[0]}</span>
                </Tooltip>
              </CircleMarker>
              <CircleMarker
                center={routeData.positions[routeData.positions.length - 1]}
                radius={9}
                pathOptions={{ color: '#fff', fillColor: '#dc2626', fillOpacity: 1, weight: 3 }}
              >
                <Tooltip direction="top" offset={[0, -8]} permanent>
                  <span className="text-[10px] font-bold text-rose-700">
                    BİTİR · {routeData.orderedPostcodes[routeData.orderedPostcodes.length - 1]}
                  </span>
                </Tooltip>
              </CircleMarker>
            </>
          )}
        </MapContainer>
      </div>

      <div className="text-[10px] text-slate-400">
        Tile: OpenStreetMap · Geocoding: Nominatim (ilk açılışta ~{postcodes.length}s sürebilir, sonrasında cache).
        Rota çizgisi TVI (Tijdvenster) sırasıyla.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HISTORY TAB — Son 24 saatte Ritmonitor üzerinde değişim
// ---------------------------------------------------------------------------
function HistoryTab({ ritnaam }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/postnl/ritmonitor/history/${encodeURIComponent(ritnaam)}?hours=24`)
      .then((res) => {
        if (!cancelled) setData(res.data?.data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || 'History yüklenemedi');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ritnaam]);

  if (loading) return <LoaderBlock label="History yükleniyor…" />;
  if (error) return <ErrorBlock msg={String(error)} />;
  if (!data?.entries?.length) {
    return <EmptyBlock msg="Bu rota için henüz history yok. Bekleyin — her 5 dk'da bir snapshot eklenecek." />;
  }

  const entries = data.entries;
  const first = entries[0];
  const last = entries[entries.length - 1];
  const totalStops = last?.aantal_stops || first?.aantal_stops || 0;
  const startRemaining = first?.stops_te_doen || 0;
  const endRemaining = last?.stops_te_doen || 0;
  const completedInWindow = Math.max(0, startRemaining - endRemaining);

  // Sparkline: x = index, y = stops_te_doen, küçük SVG çizelge
  const maxRemain = Math.max(...entries.map((e) => e.stops_te_doen || 0), 1);
  const w = 600;
  const h = 80;
  const points = entries.map((e, i) => {
    const x = (i / Math.max(1, entries.length - 1)) * w;
    const y = h - ((e.stops_te_doen || 0) / maxRemain) * (h - 8) - 4;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Toplam Stop" value={totalStops} />
        <StatCard label="24sa'de Tamamlanan" value={completedInWindow} tone="emerald" />
        <StatCard label="Şu an Kalan" value={endRemaining} tone={endRemaining === 0 ? 'emerald' : 'amber'} />
      </div>

      <Card className="p-3">
        <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-2 flex justify-between">
          <span>Kalan stop trendi (son 24 saat, {entries.length} ölçüm)</span>
          <span className="text-slate-400">y ekseni: stops_te_doen</span>
        </div>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
          <polyline
            points={points}
            fill="none"
            stroke="#f97316"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Son nokta vurgusu */}
          {entries.length > 0 && (() => {
            const i = entries.length - 1;
            const x = (i / Math.max(1, entries.length - 1)) * w;
            const y = h - ((entries[i].stops_te_doen || 0) / maxRemain) * (h - 8) - 4;
            return <circle cx={x} cy={y} r="4" fill="#f97316" stroke="#fff" strokeWidth="2" />;
          })()}
        </svg>
        <div className="flex justify-between text-[9px] text-slate-400 mt-1">
          <span>{new Date(first.captured_at).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          <span>{new Date(last.captured_at).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto max-h-72 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800 text-slate-700">
              <tr>
                <th className="text-left px-2 py-1.5 font-semibold">Zaman</th>
                <th className="text-left px-2 py-1.5 font-semibold">Şoför</th>
                <th className="text-left px-2 py-1.5 font-semibold">Son Aksiyon</th>
                <th className="text-right px-2 py-1.5 font-semibold">Toplam</th>
                <th className="text-right px-2 py-1.5 font-semibold">Kalan</th>
                <th className="text-right px-2 py-1.5 font-semibold">İlerleme</th>
              </tr>
            </thead>
            <tbody>
              {[...entries].reverse().map((e, i) => {
                const total = e.aantal_stops || 0;
                const left = e.stops_te_doen || 0;
                const done = Math.max(0, total - left);
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-700 odd:bg-slate-50/40 dark:odd:bg-slate-900/40">
                    <td className="px-2 py-1 font-mono">
                      {new Date(e.captured_at).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                    </td>
                    <td className="px-2 py-1">{e.chauffeur || '—'}</td>
                    <td className="px-2 py-1 font-mono">{e.tijdstip_laatste_actie || '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{total}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-semibold">
                      {left === 0 && total > 0 ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">✓</Badge> : left}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function LoaderBlock({ label }) {
  return (
    <div className="text-center py-12 text-slate-500 flex items-center justify-center gap-2 text-sm">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  );
}

function ErrorBlock({ msg }) {
  return (
    <Card className="p-4 border-rose-200 bg-rose-50 dark:bg-rose-900/20">
      <div className="flex items-start gap-2 text-rose-700">
        <AlertCircle className="h-4 w-4 mt-0.5" />
        <div className="text-sm">{msg}</div>
      </div>
    </Card>
  );
}

function EmptyBlock({ msg }) {
  return (
    <div className="text-center py-12">
      <Package className="h-8 w-8 text-slate-300 mx-auto mb-2" />
      <div className="text-sm text-slate-500">{msg}</div>
    </div>
  );
}

function StatCard({ label, value, tone = 'slate' }) {
  const toneCls = {
    slate: 'text-slate-900 dark:text-slate-100',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
  }[tone] || 'text-slate-900';
  return (
    <Card className="p-3 text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
    </Card>
  );
}
